/**
 * @file 路由
 * @author gaoxunchen
 */

import _ from 'lodash';
import React, { useEffect } from 'react';
import { Route, Switch, Redirect, matchPath } from 'react-router-dom';
import ReactDOM from 'react-dom';

import BasePage from '@/business-components/base-page';
import TabNav from '@/business-components/tab-nav';
import globalStore from '@/stores';
import useMicroApp from '@/hooks/useMicroApp';

import Forbidden from './error/Forbidden';
import ErrorBoundary from './ErrorBoundary';
import { Alert } from '@tencent/spaui';
import { User } from '@/model/user';
import { reportUserBehavior } from '@/pages/agency/utils/util';
import Download from '@/common-components/Download';
import { stringToReactElement } from '@/utils/jsx';
import reportHub from '@/common/report-hub';

const renderRoute = (Component: any, renderProps: Record<string, any>, basePage = false) => {
  const pageNotice = Object.keys(globalStore.pageNoticeConfig).find((key) => matchPath(renderProps.location.pathname, key));
  const { noticeType, message, ...noticeProps } = globalStore.pageNoticeConfig[pageNotice || ''] || {};
  const noticeContent = message && noticeType === 'html' ? stringToReactElement(message) : message;

  if (basePage) {
    return <>
      {pageNotice ? <div className="page-notice force"><Alert type="danger" {...noticeProps} message={noticeContent} /></div> : null}
      <Component {...renderProps} />
    </>;
  }
  return (
    <div className="page-container">
      {pageNotice ? <Alert type="danger" {...noticeProps} message={noticeContent} /> : null}
      <TabNav />
      <ErrorBoundary>
        <Component {...renderProps} />
      </ErrorBoundary>
    </div>
  );
};

const Routes: React.FC<{ account: User }> = (props) => {
  const { account } = props;
  const { user_id, company_id, agency_uid, agency_list, bm_id, siteMap, feature_flag } = account;
  const { routes, redirectUrl } = siteMap;

  // 注册微应用
  const {
    microAppConfigLoaded,
    isMicroAppPath,
    basePageOptions,
  } = useMicroApp({
    redirectUrl,
    userInfo: {
      name: `${company_id}|${user_id}`,
      user_id,
      bm_id,
      agency_uid,
      agency_uid_list: agency_list?.map((item) => item.agency_uid),
      company_id,
      feature_flag,
      account,
    },
    agencyCommon: {
      reportUserBehavior,
      Download,
      reportHub,
    },
  });

  useEffect(() => {
    // 非子应用，移除#microapp-loading内容
    if (microAppConfigLoaded && !isMicroAppPath) {
      const subAppLoading = document.querySelector('#microapp-loading') as HTMLElement;
      subAppLoading && ReactDOM.unmountComponentAtNode(subAppLoading);
    }
  }, [microAppConfigLoaded, isMicroAppPath]);

  return (
    <>
      {/* 微应用 */}
      <BasePage
        {...basePageOptions}
        bodyComponent={<>
          <div id="microapp-container" />
          <div id="microapp-loading" />
        </>}
        className="micro-page"
        style={{ display: isMicroAppPath ? 'block' : 'none' }}
      />
      <div >
      </div>
      {microAppConfigLoaded && !isMicroAppPath ? (
        <Switch>
          <Redirect exact from="/" to={redirectUrl + location.search} />
          {
            routes.map((item: any) => {
              return (
                <Route
                  key={item.href}
                  path={item.path || item.href}
                  render={(renderProps) => renderRoute(item.component, renderProps, item.basePage)}
                />
              );
            })
          }
          <Route
            path={'/'}
            render={(props) => (
              <div className="page-container">
                <Forbidden {...props} redirectUrl={redirectUrl} />
              </div>
            )}
          />
        </Switch>
      ) : null}
    </>
  );
};

export default Routes;
